import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash, MagnifyingGlass, Warning, ShieldWarning } from "@phosphor-icons/react";

/**
 * Super-Admin · Account deletion
 * ──────────────────────────────
 * Search customers by email/name/user_id (same lookup endpoint as the
 * existing Customer Lookup page), pick the target row, preview how many
 * rows will be wiped across every user-scoped collection, then type the
 * exact email to confirm and permanently delete.
 *
 * Backend:
 *   GET  /api/admin/customer-lookup?q=...             (existing)
 *   GET  /api/super-admin/accounts/preview-delete?user_id=...
 *   POST /api/super-admin/accounts/delete             {user_id, confirm_email, reason}
 */
export default function AdminAccountDeletePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);   // {user_id, email, name, ...}
  const [preview, setPreview] = useState(null);     // backend preview-delete response
  const [previewing, setPreviewing] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Auth-gate: wait until AuthContext resolves; non-admin → /
  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSelected(null);
    setPreview(null);
    setConfirmEmail("");
    try {
      const { data } = await api.get("/admin/customer-lookup", { params: { q, limit: 10 } });
      setResults(data?.results || []);
      if (!data?.results?.length) toast(`No customer matched '${q}'.`, { icon: "🔍" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const loadPreview = async (row) => {
    const cust = row.customer || row;
    setSelected(cust);
    setPreview(null);
    setConfirmEmail("");
    setReason("");
    setPreviewing(true);
    try {
      const { data } = await api.get("/super-admin/accounts/preview-delete", {
        params: { user_id: cust.user_id },
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load deletion preview");
      setSelected(null);
    } finally {
      setPreviewing(false);
    }
  };

  const commitDelete = async () => {
    if (!selected || !preview?.deletable) return;
    if (confirmEmail.trim().toLowerCase() !== (selected.email || "").toLowerCase()) {
      toast.error("Typed email does not match the target account.");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.post("/super-admin/accounts/delete", {
        user_id: selected.user_id,
        confirm_email: confirmEmail,
        reason: reason || undefined,
      });
      toast.success(`Deleted — ${data.rows_deleted} rows wiped across all collections.`);
      // Reset state
      setSelected(null);
      setPreview(null);
      setConfirmEmail("");
      setReason("");
      setResults((prev) => prev.filter((r) => (r.customer || r).user_id !== data.user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto py-12 px-4 text-muted">Loading&hellip;</div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <main className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-account-delete-page">
        <div className="flex items-center gap-3 mb-2">
          <Trash size={28} weight="duotone" className="text-rose-600" />
          <h1 className="font-serif-display text-3xl sm:text-4xl font-light">Delete Account</h1>
        </div>
        <p className="text-sm text-muted mb-6 max-w-2xl">
          Search a signed-up customer by email, name or user-id, preview every
          piece of data that will be wiped, then confirm by typing the
          account&rsquo;s exact email. Permanently destroys the customer
          record plus chats, messages, wallet orders, reports, push
          subscriptions and every other user-scoped row across the database.
        </p>

        {/* SEARCH */}
        <form onSubmit={runSearch} className="flex flex-col sm:flex-row gap-2 mb-6" data-testid="account-search-form">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Email, name or user_id"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-stone-300 bg-white text-sm focus:ring-2 focus:ring-amber-400 outline-none"
              data-testid="account-search-input"
            />
          </div>
          <button
            type="submit"
            disabled={searching || !query.trim()}
            className="px-5 py-2.5 rounded-md bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 disabled:opacity-60"
            data-testid="account-search-submit"
          >
            {searching ? "Searching\u2026" : "Search"}
          </button>
        </form>

        {/* RESULTS LIST */}
        {results.length > 0 && (
          <div className="mb-8" data-testid="account-search-results">
            <h2 className="text-xs uppercase tracking-wider text-muted mb-2">Matches ({results.length})</h2>
            <div className="space-y-2">
              {results.map((row) => {
                const c = row.customer || row;
                return (
                  <button
                    key={c.user_id}
                    type="button"
                    onClick={() => loadPreview(row)}
                    className={`w-full text-left p-3 rounded-md border transition ${
                      selected?.user_id === c.user_id
                        ? "border-rose-400 bg-rose-50"
                        : "border-stone-200 bg-white hover:border-stone-400"
                    }`}
                    data-testid={`account-result-${c.user_id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name || "(no name)"}</div>
                        <div className="text-xs text-muted truncate">{c.email}</div>
                        <div className="text-[11px] text-muted mt-0.5 font-mono">{c.user_id}</div>
                      </div>
                      <div className="text-right text-xs flex-shrink-0">
                        <div className="font-mono">{`\u20B9 ${(c.wallet_balance || 0).toFixed(0)}`}</div>
                        <div className="text-muted">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* PREVIEW + CONFIRM */}
        {previewing && (
          <div className="text-muted text-sm" data-testid="account-preview-loading">Loading deletion plan&hellip;</div>
        )}
        {preview && selected && (
          <div className="card-surface rounded-lg p-5 sm:p-6 border-2 border-rose-300 bg-rose-50/50" data-testid="account-preview-panel">
            <div className="flex items-start gap-3 mb-4">
              <Warning size={24} weight="duotone" className="text-rose-700 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-base text-rose-900">Permanent deletion preview</h3>
                <p className="text-xs text-rose-800 mt-1">
                  This action cannot be undone. {preview.total_rows} row{preview.total_rows === 1 ? "" : "s"} will be wiped across {preview.plan.length} collection{preview.plan.length === 1 ? "" : "s"}.
                </p>
              </div>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs mb-4">
              <div><dt className="text-muted">Name</dt><dd className="font-semibold">{preview.name || "—"}</dd></div>
              <div><dt className="text-muted">Email</dt><dd className="font-semibold break-all">{preview.email}</dd></div>
              <div><dt className="text-muted">User-ID</dt><dd className="font-mono text-[11px]">{preview.user_id}</dd></div>
              <div><dt className="text-muted">Wallet</dt><dd className="font-mono">{`\u20B9 ${(preview.wallet_balance || 0).toFixed(2)}`}</dd></div>
            </dl>

            {/* PLAN TABLE */}
            <div className="mb-4 max-h-60 overflow-y-auto rounded-md border border-stone-200 bg-white">
              <table className="w-full text-xs" data-testid="account-deletion-plan">
                <thead className="bg-stone-100 sticky top-0">
                  <tr><th className="text-left px-3 py-1.5 font-semibold">Collection</th><th className="text-right px-3 py-1.5 font-semibold">Rows</th></tr>
                </thead>
                <tbody>
                  {preview.plan.map((p) => (
                    <tr key={p.collection} className="border-t border-stone-100">
                      <td className="px-3 py-1.5">{p.collection}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{p.rows}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* BLOCKED */}
            {preview.blocks?.length > 0 && (
              <div className="px-3 py-2 rounded-md bg-stone-900 text-white text-xs flex items-start gap-2 mb-4" data-testid="account-deletion-blocks">
                <ShieldWarning size={14} weight="duotone" className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">Deletion blocked</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {preview.blocks.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* CONFIRMATION */}
            {preview.deletable && (
              <>
                <label className="block text-xs text-rose-900 font-semibold mb-1.5" htmlFor="confirm-email-input">
                  Type the exact email <code className="px-1 bg-rose-100 rounded">{preview.email}</code> to confirm:
                </label>
                <input
                  id="confirm-email-input"
                  type="text"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={preview.email}
                  autoComplete="off"
                  className="w-full px-3 py-2 rounded-md border border-rose-300 bg-white text-sm font-mono focus:ring-2 focus:ring-rose-500 outline-none mb-3"
                  data-testid="account-confirm-email-input"
                />
                <label className="block text-xs text-rose-900 font-semibold mb-1.5" htmlFor="delete-reason-input">
                  Reason (optional, for audit log)
                </label>
                <textarea
                  id="delete-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-md border border-stone-300 bg-white text-sm focus:ring-2 focus:ring-amber-400 outline-none mb-4"
                  placeholder="GDPR request, duplicate signup, abuse, etc."
                  data-testid="account-delete-reason"
                />
                <button
                  type="button"
                  onClick={commitDelete}
                  disabled={deleting || confirmEmail.trim().toLowerCase() !== preview.email.toLowerCase()}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-md bg-rose-600 text-white text-sm font-bold hover:bg-rose-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="account-confirm-delete-btn"
                >
                  {deleting ? "Deleting\u2026" : `Permanently delete ${preview.email}`}
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </PageShell>
  );
}
